import React, { useCallback, useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import moment from "moment";
import ModalMobile from "../../../../components/shared/Modal/ModalMobile";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import Select from "../../../../components/shared/Select/Select";
import Checkbox from "../../../../components/shared/Checkbox";
import { listOfSubscriptions } from "../../../../common/envConfig";
import { adminSearchService } from "../../../../services/adminSearch";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchSubscriptions } from "../../hooks/useAdminSearchSubscriptions";
import { toast } from "react-toastify";

const stringFields = [
  "userEmail",
  "userName",
  "productId",
  "transactionId",
  "secondarySubType",
];
const dateFields = ["startDate", "endDate"];

const secondarySubTypeOptions = [
  { text: "None", value: "none" },
  { text: "Free", value: listOfSubscriptions.STUDENT_FREE },
  { text: "3D Player", value: listOfSubscriptions.STUDENT_LITE_ANNUALLY },
  { text: "3D Pro", value: listOfSubscriptions.PREMIUM_ANNUALLY },
];

const pickDirtyValues = (values, dirty) => {
  if (!dirty || typeof dirty !== "object") return {};

  return Object.keys(dirty).reduce((acc, key) => {
    const isDirty = dirty[key];
    if (isDirty === true) {
      acc[key] = values[key];
    } else if (isDirty && typeof isDirty === "object") {
      const nested = pickDirtyValues(values[key] || {}, isDirty);
      if (Object.keys(nested).length > 0) {
        acc[key] = nested;
      }
    }
    return acc;
  }, {});
};

const formatDateValue = (value) => {
  if (!value) return "";
  const asNumber = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(asNumber)) return "";
  return moment(asNumber).format("YYYY-MM-DD");
};

const mapSubscriptionToForm = (subscription) => {
  const base = {
    userEmail: "",
    userName: "",
    startDate: "",
    endDate: "",
    productId: "",
    autoRenewal: false,
    transactionId: "",
    secondarySubType: "none",
  };

  if (!subscription) return base;

  return {
    ...base,
    userEmail: subscription.userEmail || "",
    userName: subscription.userName || "",
    startDate: formatDateValue(subscription.startDate),
    endDate: formatDateValue(subscription.endDate),
    productId: subscription.productId || "",
    autoRenewal: Boolean(subscription.autoRenewal),
    transactionId: subscription.transactionId || "",
    secondarySubType: subscription.secondarySubType || "none",
  };
};

const normalizePayload = (values) => {
  const payload = { ...values };

  dateFields.forEach((field) => {
    if (!(field in payload)) return;
    const raw = payload[field];
    if (!raw) {
      payload[field] = null;
      return;
    }
    const parsed = moment(raw, "YYYY-MM-DD", true);
    if (parsed.isValid()) {
      payload[field] = parsed.valueOf();
    } else {
      delete payload[field];
    }
  });

  stringFields.forEach((field) => {
    if (!(field in payload)) return;
    if (typeof payload[field] === "undefined") {
      delete payload[field];
    }
  });

  if ("autoRenewal" in payload) {
    payload.autoRenewal = Boolean(payload.autoRenewal);
  }

  return payload;
};

export default function EditSubscriptionModal({ isOpen, onClose }) {
  const invalidate = useInvalidate();

  const { subscriptionsState } = useAdminSearchStore();

  const { selectedSubscriptionId } = subscriptionsState;

  const { data, isLoading, error } = useAdminSearchSubscriptions();
  const subscriptions = data?.subscriptions || [];

  const subscriptionSummary = subscriptions.find(
    (s) => s?.id === selectedSubscriptionId
  );

  const productIdOptions = useMemo(
    () =>
      Object.entries(listOfSubscriptions || {}).map(([key, value]) => ({
        text: key,
        value,
      })),
    []
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { dirtyFields, isDirty },
  } = useForm({
    defaultValues: mapSubscriptionToForm(subscriptionSummary),
  });

  useEffect(() => {
    if (subscriptionSummary) {
      reset(mapSubscriptionToForm(subscriptionSummary));
    }
  }, [subscriptionSummary, reset]);

  const mutation = useMutation(
    async ({ id, data: subscriptionData, secondarySubType }) => {
      return adminSearchService.adminSearchUpdateSubscription({
        id,
        data: subscriptionData,
        secondarySubType,
        updateFirebase: true,
      });
    },
    {
      onSuccess: async () => {
        invalidate(["adminSearchSubscriptions_key"]);
      },
    }
  );

  const productIdValue = watch("productId");
  const secondarySubTypeValue = watch("secondarySubType");
  const autoRenewalValue = watch("autoRenewal");

  const handleSelectChange = useCallback(
    (field, value, currentValue) => {
      const nextValue = value === currentValue ? "" : value;
      setValue(field, nextValue ?? "", {
        shouldDirty: true,
        shouldValidate: false,
      });
    },
    [setValue]
  );

  const onSubmit = useCallback(
    async (values) => {
      if (!selectedSubscriptionId) return;
      const dirtyValues = pickDirtyValues(values, dirtyFields);
      const normalized = normalizePayload(dirtyValues);
      const { secondarySubType, ...subscriptionData } = normalized;
      const hasSubscriptionData = Object.keys(subscriptionData).length > 0;
      const hasSecondarySubType = secondarySubType !== undefined;

      if (!hasSubscriptionData && !hasSecondarySubType) {
        toast.info("No changes to save.");
        return;
      }

      try {
        await mutation.mutateAsync({
          id: selectedSubscriptionId,
          data: hasSubscriptionData ? subscriptionData : undefined,
          secondarySubType: hasSecondarySubType ? secondarySubType : undefined,
        });
        toast.success("Changes saved.");
        onClose?.();
      } catch (err) {
        console.error("Failed to update subscription", err);
        toast.error(err?.message || "Failed to update subscription");
      }
    },
    [dirtyFields, mutation, onClose, selectedSubscriptionId]
  );

  const isSaving = mutation.isPending;
  const isBusy = isSaving;

  if (!isOpen || !selectedSubscriptionId) return null;

  return (
    <ModalMobile
      className="font-manrope w-full max-w-4xl pt-10"
      isOpen={isOpen}
      onClose={onClose}
      showClose={true}
    >
      <div className="px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold text-gray900">
              Edit subscription
            </div>
            <div className="text-base text-gray600">
              {subscriptionSummary?.userEmail || selectedSubscriptionId}
            </div>
          </div>
        </div>

        <Divider className="my-4" color="gray200" type="horizontal" />

        <form className="flex flex-col gap-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              control={control}
              name="userEmail"
              label="User email"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="userName"
              label="User name"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="startDate"
              label="Start date"
              type="date"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
            <Input
              control={control}
              name="endDate"
              label="End date"
              type="date"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />

            <div className="flex flex-col gap-2">
              <label className="text-base text-gray700">Product id</label>
              <Select
                options={productIdOptions}
                value={productIdValue || null}
                placeholder="Select product"
                withCheckbox
                disabled={isBusy}
                buttonProps={{ size: "md" }}
                onChange={(val) =>
                  handleSelectChange(
                    "productId",
                    val ?? "",
                    productIdValue ?? ""
                  )
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-base text-gray700">
                Secondary sub type
              </label>
              <Select
                options={secondarySubTypeOptions}
                value={secondarySubTypeValue || "none"}
                placeholder="Select secondary sub type"
                withCheckbox
                disabled={isBusy}
                buttonProps={{ size: "md" }}
                onChange={(val) =>
                  setValue("secondarySubType", val || "none", {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
              />
            </div>

            <Input
              control={control}
              name="transactionId"
              label="Transaction id"
              rules={{ required: false }}
              isDisabled={isBusy}
              size="md"
            />
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Checkbox
                isChecked={Boolean(autoRenewalValue)}
                isDisabled={isBusy}
                onChange={(checked) =>
                  setValue("autoRenewal", checked, {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
              />
              <span className="text-base text-gray800">Auto renewal</span>
            </div>
          </div>
        </form>
      </div>

      <Divider className="mt-6" color="gray200" type="horizontal" />

      <div className="flex items-center justify-between gap-3 px-6 py-4">
        <div className="text-base text-gray600">
          {isSaving && "Saving changes…"}
          {!isSaving && mutation.error
            ? mutation.error?.message || "Failed to update subscription"
            : null}
        </div>
        <div className="flex gap-2">
          <Button
            color="gray100"
            onClick={onClose}
            size="md"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isSaving}
            size="md"
            isDisabled={!isDirty || isSaving}
          >
            Save changes
          </Button>
        </div>
      </div>
    </ModalMobile>
  );
}
